import { canonicalStaffEmail } from "../constants/ticketAssignees";
import { ITicket, ITicketNote, TicketStatus } from "../models/Tickets/Ticket";

/** Public = visible to customer (not internal). */
export function isPublicNote(note: ITicketNote): boolean {
  return !note.isInternal;
}

export function isPublicStaffNote(note: ITicketNote): boolean {
  if (note.isInternal) return false;
  return (
    note.isFromStaff === true ||
    (note.authorEmail != null && canonicalStaffEmail(note.authorEmail) !== undefined)
  );
}

export function isPublicCustomerNote(note: ITicketNote): boolean {
  return isPublicNote(note) && !isPublicStaffNote(note);
}

export function countPublicNoteReplies(ticket: ITicket): {
  customer: number;
  staff: number;
} {
  let customer = 0;
  let staff = 0;
  for (const n of ticket.notes ?? []) {
    if (!isPublicNote(n)) continue;
    if (isPublicStaffNote(n)) staff += 1;
    else customer += 1;
  }
  return { customer, staff };
}

/** True when there is at least one public note and the newest one is from the customer (not staff). */
export function isLastPublicNoteFromCustomer(ticket: ITicket): boolean {
  const publicNotes = [...(ticket.notes ?? [])]
    .filter(isPublicNote)
    .sort((a, b) => new Date(a.created).getTime() - new Date(b.created).getTime());
  if (publicNotes.length === 0) return false;
  return !isPublicStaffNote(publicNotes[publicNotes.length - 1]);
}

/**
 * Show in ticket activity / bell when the customer has at least one public reply (note)
 * and is waiting for a public staff reply (not description-only / new tickets).
 * If staff has replied before, the latest public message must still be from the customer.
 * Resolved / closed tickets are excluded.
 */
export function isAwaitingStaffPublicReply(ticket: ITicket): boolean {
  if (ticket.status === TicketStatus.Resolved || ticket.status === TicketStatus.Closed) {
    return false;
  }

  const { customer: customerPublicCount, staff: staffPublicCount } =
    countPublicNoteReplies(ticket);
  if (customerPublicCount === 0) return false;

  if (staffPublicCount === 0) {
    return true;
  }

  const publicNotes = [...(ticket.notes ?? [])]
    .filter(isPublicNote)
    .sort((a, b) => new Date(a.created).getTime() - new Date(b.created).getTime());
  if (publicNotes.length === 0) return false;

  const lastPublic = publicNotes[publicNotes.length - 1];
  return !isPublicStaffNote(lastPublic);
}
